import {
  ActivateUserArgs,
  CreateValues,
  CreateUserResponse,
  FindAllOptions,
  ImportRow,
  Role,
  UpdateValues,
  User,
  UserDetails,
  UserWithOrganizations,
  UserWithEhrProviderInformation,
} from './models/user';
import { Common } from './common';
import { ImportValidation } from './models/provider';
import { Organization } from './models/organization';
import { Practice } from './models/practice';
import { PracticeGroup } from './models/practiceGroup';
import { SuccessResponse } from './models/common';
import { UserGroupWithDetails } from './models/user/group';

export class API {
  common: Common;

  constructor(common: Common) {
    this.common = common;
  }

  async findAll(options: FindAllOptions = {}): Promise<{ users: User[] }> {
    const url = this.common.constructPath('/users', options);
    return this.common.getJSON(url);
  }

  async create(values: CreateValues): Promise<CreateUserResponse> {
    const url = this.common.constructPath('/auth/create');
    return this.common.postJSON(url, values);
  }

  async register(values: {
    password: string;
    repeatedPassword: string;

    // Only required if you're not logged in as an unregistered user
    email?: string;
    lastName?: string;
    registrationCode?: string;
  }): Promise<SuccessResponse> {
    const url = this.common.constructPath('/auth/register');
    return this.common.postJSON(url, values);
  }

  async findOneByEmail(email: string): Promise<User> {
    const url = this.common.constructPath(`/users/${email}`);
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async findOneById(id: string): Promise<User> {
    const url = this.common.constructPath(`/users/${id}`);
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async findOneByIdWithEhrProviderInformation(
    id: string,
  ): Promise<UserWithEhrProviderInformation> {
    const url = this.common.constructPath(`/users/${id}`);
    return await this.common.getJSON(url);
  }

  async login(
    email: string,
    password: string,
  ): Promise<{ success: true; user: User; needs2fa: boolean }> {
    const url = this.common.constructPath('/auth/login');
    return this.common.postJSON(url, { email, password });
  }

  async getUserDetails(email: string): Promise<UserDetails> {
    const url = this.common.constructPath('/auth/userDetails', { email });
    return this.common.getJSON(url);
  }

  async sendSmsCode(
    code: string,
  ): Promise<{ success: boolean; message?: string }> {
    const url = this.common.constructPath('/auth/smscode');
    return this.common.postJSON(url, { code });
  }

  async logout(): Promise<SuccessResponse> {
    const url = this.common.constructPath('/auth/logout');
    return this.common.getJSON(url);
  }

  async isLoggedIn(): Promise<boolean> {
    const url = this.common.constructPath('/auth/loggedIn');
    const { isAuthenticated } = await this.common.getJSON(url);
    return isAuthenticated;
  }

  async me(): Promise<UserWithOrganizations> {
    const url = this.common.constructPath('/auth/me');
    const { user } = await this.common.getJSON(url);
    return user;
  }

  async activateProvisionalUser(
    values: ActivateUserArgs,
  ): Promise<CreateUserResponse> {
    const url = this.common.constructPath('/auth/activate');
    return this.common.postJSON(url, values);
  }

  async update(userId: string, values: UpdateValues): Promise<User> {
    const url = this.common.constructPath(`/users/${userId}`);
    return this.common.patchJSON(url, values);
  }

  async remove(userId: string): Promise<User> {
    const url = this.common.constructPath(`/users/${userId}`);
    return this.common.deleteJSON(url);
  }

  async recover(userId: string): Promise<User> {
    const url = this.common.constructPath(`/users/${userId}/recover`);
    return this.common.postJSON(url, {});
  }

  async resetPassword(
    userId: string,
  ): Promise<{ user: User; userRegistrationCode: string }> {
    const url = this.common.constructPath(`/users/${userId}/reset-password`);
    return this.common.postJSON(url, {});
  }

  async updateRole(userId: string, role: Role): Promise<User> {
    const url = this.common.constructPath(`/users/${userId}/role`);
    return this.common.patchJSON(url, { role });
  }

  async findAllGroups(userId: string): Promise<UserGroupWithDetails[]> {
    const url = this.common.constructPath(`/users/${userId}/groups`);
    return this.common.getJSON(url);
  }

  async findAllOrganizations(userId: string): Promise<Organization[]> {
    const url = this.common.constructPath(`/users/${userId}/organizations`);
    return this.common.getJSON(url);
  }

  async addOrganization(
    userId: string,
    organizationId: string,
  ): Promise<Organization> {
    const url = this.common.constructPath(
      `/users/${userId}/organizations/${organizationId}`,
    );
    return this.common.postJSON(url, {});
  }

  async removeOrganization(
    userId: string,
    organizationId: string,
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `/users/${userId}/organizations/${organizationId}`,
    );
    return this.common.deleteJSON(url);
  }

  async findAllPracticeGroups(
    userId: string,
  ): Promise<(PracticeGroup & { userPracticeGroupId: string })[]> {
    const url = this.common.constructPath(`/users/${userId}/practice-groups`);
    return this.common.getJSON(url);
  }

  async addPracticeGroup(
    userId: string,
    practiceGroupId: string,
  ): Promise<PracticeGroup & { userPracticeGroupId: string }> {
    const url = this.common.constructPath(
      `/users/${userId}/practice-groups/${practiceGroupId}`,
    );
    return this.common.postJSON(url, {});
  }

  async removePracticeGroup(
    userId: string,
    practiceGroupId: string,
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `/users/${userId}/practice-groups/${practiceGroupId}`,
    );
    return this.common.deleteJSON(url);
  }

  async findAllPractices(
    userId: string,
  ): Promise<(Practice & { userPracticeId: string })[]> {
    const url = this.common.constructPath(`/users/${userId}/practices`);
    return this.common.getJSON(url);
  }

  async addPractice(
    userId: string,
    practiceId: string,
  ): Promise<Practice & { userPracticeId: string }> {
    const url = this.common.constructPath(
      `/users/${userId}/practices/${practiceId}`,
    );
    return this.common.postJSON(url, {});
  }

  async removePractice(
    userId: string,
    practiceId: string,
  ): Promise<SuccessResponse> {
    const url = this.common.constructPath(
      `/users/${userId}/practices/${practiceId}`,
    );
    return this.common.deleteJSON(url);
  }

  async importVerify(
    values: Record<string, string>[],
  ): Promise<Record<string, ImportValidation>[]> {
    const url = this.common.constructPath(`/users/import/verify`);
    return this.common.postJSON(url, { providers: values });
  }

  async import(values: ImportRow[]) {
    const url = this.common.constructPath('/users');
    return this.common.putJSON(url, { providers: values });
  }
}
